import CustomerService from "@/services/api/customer";
import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";
import UserService from "@/services/api/user";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";

interface LoadingState {
    distributor: boolean;
    getCustomerDetails: boolean;
    meetingAnalysis: boolean;
    getStatusList: boolean;
    getActionPointList: boolean;
    getCustomerConfigurationCustomFields: boolean;
    getCustomFields: boolean;
    getCustomerData: boolean;
    getLeadSource: boolean;
    getLeadType: boolean;
    getLeadStage: boolean;
    getTerritories: boolean;
    getReferalUser: boolean;
    getCountryList: boolean;
    getStateList: boolean;
    getCityList: boolean;
    getLocalityList: boolean;
    getLeadbyNextLeadType: boolean;
    getLeadDesignations: boolean;
    getCRMSettings: boolean;
    getLeadContacts: boolean;
    getAddress: boolean;
    getCustomerBulkReport: boolean;
    viewCustomerBulkReport: boolean;
    exportCustomerBulkUploadReport: boolean;
    exportCustomerReportDownloadFile: boolean;
    customerExportLoading: boolean;
    auditLogLoading: boolean;
    getTransactionBulkReport: boolean;
    viewTransactionBulkReport: boolean;
    exportTransactionReportDownloadFile: boolean;
    leadRequestLoading: boolean;
    updateLeadRequestLoading: boolean;
    getLeadRequestDetailLoading: boolean;
}

interface CustomerState {
    customers: any[];
    statusList: any[],
    leadSource: any[],
    leadType: any[],
    leadStage: any[],
    Territory: any[],
    ReferralUser: any[],
    actionPointList: any[],
    customerCustomFields: any[],
    customFields: any[],
    customerData: any,
    assignBulkCustomer: any[],
    error: string | null;
    refreshCustomerDetails: boolean,
    refreshCustomerList: boolean
    addNewCustomer: any[],
    countryList: any[],
    stateList: any[],
    cityList: any[],
    localityList: any[],
    leadbyNextLead: any[],
    distributorList: {
        Items: any[];
        TotalRecords: number;
    } | null;
    loading: LoadingState,
    leadDesignation: any[];
    crmSettings: any;
    leadContacts: any[];
    auditLog: any[];
    address: any;
    customerBulkReportList: any;
    customerBulkReportViewData: any;
    stateListByCountry: Record<number, any[]>;
    cityListByState: Record<number, any[]>;
    transactionBulkReportList: any;
    transactionBulkReportViewData: any;
    leadRequest: any[];
    updateLeadRequest: any;
    refreshLeadRequest: boolean;
    getLeadRequestDetail: any;
}

const initialState: CustomerState = {
    customers: [],
    statusList: [],
    leadSource: [],
    leadStage: [],
    leadType: [],
    Territory: [],
    ReferralUser: [],
    actionPointList: [],
    customerCustomFields: [],
    addNewCustomer: [],
    customFields: [],
    customerData: null,
    error: null,
    refreshCustomerDetails: false,
    refreshCustomerList: false,
    assignBulkCustomer: [],
    countryList: [],
    stateList: [],
    cityList: [],
    localityList: [],
    leadbyNextLead: [],
    distributorList: null,
    loading: {
        distributor: false,
        getCustomerDetails: false,
        meetingAnalysis: false,
        getStatusList: false,
        getActionPointList: false,
        getCustomerConfigurationCustomFields: false,
        getCustomFields: false,
        getCustomerData: false,
        getLeadSource: false,
        getLeadType: false,
        getLeadStage: false,
        getTerritories: false,
        getReferalUser: false,
        getCountryList: false,
        getStateList: false,
        getCityList: false,
        getLocalityList: false,
        getLeadbyNextLeadType: false,
        getLeadDesignations: false,
        getCRMSettings: false,
        getLeadContacts: false,
        getAddress: false,
        getCustomerBulkReport: false,
        viewCustomerBulkReport: false,
        exportCustomerBulkUploadReport: false,
        exportCustomerReportDownloadFile: false,
        customerExportLoading: false,
        auditLogLoading: false,
        getTransactionBulkReport: false,
        viewTransactionBulkReport: false,
        exportTransactionReportDownloadFile: false,
        leadRequestLoading: false,
        updateLeadRequestLoading: false,
        getLeadRequestDetailLoading: false,
    },
    leadDesignation: [],
    crmSettings: null,
    leadContacts: [],
    auditLog: [],
    address: null,
    customerBulkReportList: null,
    customerBulkReportViewData: null,
    stateListByCountry: {},
    cityListByState: {},
    transactionBulkReportList: null,
    transactionBulkReportViewData: null,
    leadRequest: [],
    updateLeadRequest: null,
    refreshLeadRequest: false,
    getLeadRequestDetail: null,
}

export const getCustomerDetails = createAsyncThunk(
    "customer/CustomerDetails",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getCustomersDetails(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message || "Failed to fetch customers");
        }
    }
)

export const deleteCustomer = createAsyncThunk(
    "customer/deleteCustomer",
    async (customerId: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.deleteCustomer(customerId);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message || "Failed to Delete");
        }
    }

)

export const assignCustomer = createAsyncThunk(
    "customer/assignLead",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.assignLead(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const commentOnLeadTransactions = createAsyncThunk(
    "customer/commentOnLeadTransactions",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.commentOnLeadTransactions(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const meetingAnalysis = createAsyncThunk(
    "customer/meetingAnalysis",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getMeetingAnalysis(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const getStatusList = createAsyncThunk(
    "customer/getStatusList",
    async (__, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getStatusList();
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const getActionPointList = createAsyncThunk(
    "customer/getActionPointList",
    async (__, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getActionPointList();
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const getCustomerConfigurationCustomFields = createAsyncThunk(
    "customer/getCustomerConfigurationCustomFields",
    async (__, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getCustomerConfigurationCustomFields();
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const assignLeadRoster = createAsyncThunk(
    "customer/assignLeadRoster",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.assignLeadRoster(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const addNewCustomer = createAsyncThunk(
    "customer/addNewCustomer",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.addNewCustomer(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const updateCustomerDetails = createAsyncThunk(
    "customer/updateCustomerDetails",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.updateCustomerDetails(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const getCustomFields = createAsyncThunk(
    "customer/getCustomFields",
    async (__, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getCustomFields();
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const getCustomerData = createAsyncThunk(
    "customer/getCustomerData",
    async (LeadId: number, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getCustomerData(LeadId);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }

)

export const getLeadSource = createAsyncThunk(
    "customer/getLeadSource",
    async (__, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getLeadSource();
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const getLeadType = createAsyncThunk(
    "customer/getLeadType",
    async (__, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getLeadType();
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const getLeadStage = createAsyncThunk(
    "customer/getLeadStage",
    async (__, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getLeadStage();
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)


export const getTerritories = createAsyncThunk(
    "customer/getTerritories",
    async (__, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getTerritories();
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const getReferalUser = createAsyncThunk(
    "customer/getReferalUser",
    async (__, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getReferalUser();
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const assignBulkCustomer = createAsyncThunk(
    "customer/assignBulkCustomer",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.assignBulkCustomer(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const getCountryList = createAsyncThunk(
    "customer/getCountryList",
    async (__, { rejectWithValue }) => {
        try {
            const res = await UserService.getCountryList();
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const getStateList = createAsyncThunk(
    "customer/getStateList",
    async (countryId: number, { rejectWithValue }) => {
        try {
            const res = await UserService.getStateList(countryId);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const getCityList = createAsyncThunk(
    "customer/getCityList",
    async (stateId: number, { rejectWithValue }) => {
        try {
            const res = await UserService.getCityList(stateId);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const getLocalityList = createAsyncThunk(
    "customer/getLocalityList",
    async (localityId: number, { rejectWithValue }) => {
        try {
            const res = await UserService.getLocalityList(localityId);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const getLeadbyNextLeadType = createAsyncThunk(
    "customer/getLeadbyNextLeadType",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getLeadbyNextLeadType(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const getDistributor = createAsyncThunk(
    "customer/getDistributor",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getDistributor(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
);

export const approveDistributor = createAsyncThunk(
    "customer/approveDistributor",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.approveDistributor(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
);

export const customerBulkUpload = createAsyncThunk(
    "customer/BulkUpload",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.CustomerBulkUpload(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
);

export const getLeadDesignations = createAsyncThunk(
    "customer/getLeadDesignations",
    async (_, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getLeadDesignations();
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || "Failed to fetch lead designation");
        }
    }
);

export const getCRMSettings = createAsyncThunk(
    "customer/getCRMSettings",
    async (_, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getCRMSettings();
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch crm settings");
        }
    }
);

export const getLeadContacts = createAsyncThunk(
    "customer/getLeadContacts",
    async (_, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getLeadContacts();
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch lead contacts");
        }
    }
);

export const getAddress = createAsyncThunk(
    "customer/getAddress",
    async (data: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getAddress(data);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch address");
        }
    }
);

export const getCustomerBulkReport = createAsyncThunk(
    "customer/getCustomerBulkReport",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getCustomerBulkReport(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
);

export const viewCustomerBulkReport = createAsyncThunk(
    "customer/viewCustomerBulkReport",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.viewCustomerBulkReport(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
);

export const getCustomerBulkUploadReport = createAsyncThunk(
    "customer/getCustomerBulkUploadReport",
    async (fileId: number, { rejectWithValue }) => {
        try {
            const response = await CustomerService.exportCustomerBulkUploadReport(fileId);
            return saveAsExcelFile(response, "Customer Reports");
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch user bulk upload report",
            );
        }
    },
);

export const getCustomerReportDownloadFile = createAsyncThunk(
    "customer/getCustomerReportDownloadFile",
    async (fileId: number, { rejectWithValue }) => {
        try {
            const response = await CustomerService.exportCustomerBulkUploadFile(fileId);
            return saveAsExcelFile(response, "Customer Report File");
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to export file",
            );
        }
    },
);

export const getCustomerExport = createAsyncThunk(
    "customer/getCustomerExport",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await CustomerService.ExportCustomerReport(data);
            return saveAsExcelFile(response, "Customers");
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to export",
            );
        }
    },
);

export const getAuditLog = createAsyncThunk(
    "customer/auditLog",
    async (params: any, { rejectWithValue }) => {
        try {
            const { signal, ...queryParams } = params;
            const res = await CustomerService.getAuditLog(queryParams, signal);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }
)

export const uploadCRMTransactionNotes = createAsyncThunk(
    "customer/uploadCRMTransactionNotes",
    async (data: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.uploadCRMTransactionNotes(data);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to Upload Customer Logs");
        }
    }
);

export const exportTransactionBulkUploadFile = createAsyncThunk(
    "customer/exportTransactionBulkUploadFile",
    async (fileId: number, { rejectWithValue }) => {
        try {
            const response = await CustomerService.exportTransactionBulkUploadFile(fileId);
            return saveAsExcelFile(response, "Transaction Report File");
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to export file",
            );
        }
    },
);

export const viewTransactionBulkReport = createAsyncThunk(
    "customer/viewTransactionBulkReport",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.viewTransactionBulkReport(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch view transaction report");
        }
    }
);

export const getLeadRequestData = createAsyncThunk(
    "customer/getLeadRequestData",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getLeadRequestData(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message || "Failed to fetch customer request");
        }
    }
);

export const updateLeadRequest = createAsyncThunk(
    "customer/updateLeadRequest",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await CustomerService.updateLeadRequest(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message || "Failed to fetch customer request");
        }
    }
);

export const getLeadRequestDetail = createAsyncThunk(
    "customer/getLeadRequestDetail",
    async (RequestId: number, { rejectWithValue }) => {
        try {
            const res = await CustomerService.getLeadRequestDetail(RequestId);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.Message);
        }
    }

)

const customerSlice = createSlice({
    name: "customer",
    initialState,
    reducers: {
        setRefreshCustomerDetails: (state, action) => {
            state.refreshCustomerDetails = action.payload;
        },
        setRefreshCustomerList: (state, action) => {
            state.refreshCustomerList = action.payload;
        },
        setRefreshLeadRequest: (state, action) => {
            state.refreshLeadRequest = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCustomerDetails.pending, (state) => {
                state.loading.getCustomerDetails = true;
                state.error = null;
            })
            .addCase(getCustomerDetails.fulfilled, (state, action) => {
                state.loading.getCustomerDetails = false;
                state.customers = action.payload || [];
            })
            .addCase(getCustomerDetails.rejected, (state, action: any) => {
                state.loading.getCustomerDetails = false;
                state.error = action.payload || "Failed to fetch customers";
            })

            .addCase(deleteCustomer.fulfilled, (state, action) => {
                state.customers = action.payload || []
            })

            .addCase(assignCustomer.fulfilled, (state, action) => {
                state.customers = action.payload || [];
            })

            .addCase(commentOnLeadTransactions.fulfilled, (state, action) => {
                // state.customers = action.payload || [];
            })

            .addCase(meetingAnalysis.pending, (state) => {
                state.error = null;
            })
            .addCase(meetingAnalysis.fulfilled, (state, action) => {
                // state.customers = action.payload || [];
            })
            .addCase(meetingAnalysis.rejected, (state, action: any) => {
                state.error = action.payload;
            })

            .addCase(getStatusList.pending, (state) => {
                state.loading.getStatusList = true;
                state.error = null;
            })
            .addCase(getStatusList.fulfilled, (state, action) => {
                state.loading.getStatusList = false;
                state.statusList = action.payload;
            })
            .addCase(getStatusList.rejected, (state, action: any) => {
                state.loading.getStatusList = false;
                state.error = action.payload;
            })

            .addCase(getActionPointList.pending, (state) => {
                state.loading.getActionPointList = true;
                state.error = null;
            })
            .addCase(getActionPointList.fulfilled, (state, action) => {
                state.loading.getActionPointList = false;
                state.actionPointList = action.payload;
            })
            .addCase(getActionPointList.rejected, (state, action: any) => {
                state.loading.getActionPointList = false;
                state.error = action.payload;
            })

            .addCase(getCustomerConfigurationCustomFields.pending, (state) => {
                state.loading.getCustomerConfigurationCustomFields = true;
                state.error = null;
            })
            .addCase(getCustomerConfigurationCustomFields.fulfilled, (state, action) => {
                state.loading.getCustomerConfigurationCustomFields = false;
                state.customerCustomFields = action.payload;
            })
            .addCase(getCustomerConfigurationCustomFields.rejected, (state, action: any) => {
                state.loading.getCustomerConfigurationCustomFields = false;
                state.error = action.payload;
            })

            .addCase(assignLeadRoster.fulfilled, (state, action) => {
                // state.customers = action.payload || [];
            })

            .addCase(addNewCustomer.fulfilled, (state, action) => {
                state.addNewCustomer = action.payload || [];
            })

            .addCase(getCustomFields.pending, (state) => {
                state.loading.getCustomFields = true;
                state.error = null;
            })
            .addCase(getCustomFields.fulfilled, (state, action) => {
                state.loading.getCustomFields = false;
                state.customFields = action.payload || [];
            })
            .addCase(getCustomFields.rejected, (state, action: any) => {
                state.loading.getCustomFields = false;
                state.error = action.payload;
            })

            .addCase(getCustomerData.pending, (state) => {
                state.loading.getCustomerData = true;
                state.error = null;
            })
            .addCase(getCustomerData.fulfilled, (state, action) => {
                state.loading.getCustomerData = false;
                state.customerData = action.payload || {};
            })
            .addCase(getCustomerData.rejected, (state, action: any) => {
                state.loading.getCustomerData = false;
                state.error = action.payload;
            })

            .addCase(updateCustomerDetails.fulfilled, (state, action) => {
                state.addNewCustomer = action.payload || [];
            })

            .addCase(getLeadSource.pending, (state) => {
                state.loading.getLeadSource = true;
                state.error = null;
            })
            .addCase(getLeadSource.fulfilled, (state, action) => {
                state.loading.getLeadSource = false;
                state.leadSource = action.payload;
            })
            .addCase(getLeadSource.rejected, (state, action: any) => {
                state.loading.getLeadSource = false;
                state.error = action.payload;
            })

            .addCase(getLeadType.pending, (state) => {
                state.loading.getLeadType = true;
                state.error = null;
            })
            .addCase(getLeadType.fulfilled, (state, action) => {
                state.loading.getLeadType = false;
                state.leadType = action.payload;
            })
            .addCase(getLeadType.rejected, (state, action: any) => {
                state.loading.getLeadType = false;
                state.error = action.payload;
            })

            .addCase(getLeadStage.pending, (state) => {
                state.loading.getLeadStage = true;
                state.error = null;
            })
            .addCase(getLeadStage.fulfilled, (state, action) => {
                state.loading.getLeadStage = false;
                state.leadStage = action.payload || [];
            })
            .addCase(getLeadStage.rejected, (state, action: any) => {
                state.loading.getLeadStage = false;
                state.error = action.payload;
            })

            .addCase(getTerritories.pending, (state) => {
                state.loading.getTerritories = true;
                state.error = null;
            })
            .addCase(getTerritories.fulfilled, (state, action) => {
                state.loading.getTerritories = false;
                state.Territory = action.payload || [];
            })
            .addCase(getTerritories.rejected, (state, action: any) => {
                state.loading.getTerritories = false;
                state.error = action.payload;
            })

            .addCase(getReferalUser.pending, (state) => {
                state.loading.getReferalUser = true;
                state.error = null;
            })
            .addCase(getReferalUser.fulfilled, (state, action) => {
                state.loading.getReferalUser = false;
                state.ReferralUser = action.payload || [];
            })
            .addCase(getReferalUser.rejected, (state, action: any) => {
                state.loading.getReferalUser = false;
                state.error = action.payload;
            })

            .addCase(assignBulkCustomer.fulfilled, (state, action) => {
                state.assignBulkCustomer = action.payload || [];
            })

            .addCase(getCountryList.pending, (state) => {
                state.loading.getCountryList = true;
                state.error = null;
            })
            .addCase(getCountryList.fulfilled, (state, action) => {
                state.loading.getCountryList = false;
                state.countryList = action.payload || [];
                state.stateList = [];
                state.cityList = [];
                state.localityList = [];
            })
            .addCase(getCountryList.rejected, (state, action: any) => {
                state.loading.getCountryList = false;
                state.error = action.payload;
            })
            .addCase(getStateList.pending, (state) => {
                state.loading.getStateList = true;
                state.error = null;
            })
            .addCase(getStateList.fulfilled, (state, action) => {
                state.loading.getStateList = false;
                state.stateList = action.payload || [];
                state.stateListByCountry[action.meta.arg] = action.payload;
                state.cityList = [];
                state.localityList = [];
            })
            .addCase(getStateList.rejected, (state, action: any) => {
                state.loading.getStateList = false;
                state.error = action.payload;
            })
            .addCase(getCityList.pending, (state) => {
                state.loading.getCityList = true;
                state.error = null;
            })
            .addCase(getCityList.fulfilled, (state, action) => {
                state.loading.getCityList = false;
                state.cityList = action.payload || [];
                state.cityListByState[action.meta.arg] = action.payload;
                state.localityList = [];
            })
            .addCase(getCityList.rejected, (state, action: any) => {
                state.loading.getCityList = false;
                state.error = action.payload;
            })
            .addCase(getLocalityList.pending, (state) => {
                state.loading.getLocalityList = true;
                state.error = null;
            })
            .addCase(getLocalityList.fulfilled, (state, action) => {
                state.loading.getLocalityList = false;
                state.localityList = action.payload || [];
            })
            .addCase(getLocalityList.rejected, (state, action: any) => {
                state.loading.getLocalityList = false;
                state.error = action.payload;
            })

            .addCase(getLeadbyNextLeadType.pending, (state) => {
                state.loading.getLeadbyNextLeadType = true;
                state.error = null;
            })
            .addCase(getLeadbyNextLeadType.fulfilled, (state, action) => {
                state.loading.getLeadbyNextLeadType = false;
                state.leadbyNextLead = action.payload || [];
            })
            .addCase(getLeadbyNextLeadType.rejected, (state, action: any) => {
                state.loading.getLeadbyNextLeadType = false;
                state.error = action.payload;
            })
            .addCase(getDistributor.pending, (state) => {
                state.loading.distributor = true;
                state.error = null;
                state.distributorList = null;
            })
            .addCase(getDistributor.fulfilled, (state, action) => {
                state.loading.distributor = false;
                state.distributorList = action.payload ?? {
                    Items: [],
                    TotalRecords: 0,
                };
            })
            .addCase(getDistributor.rejected, (state, action) => {
                state.loading.distributor = false;
                state.error = action.payload as string;
            })
            .addCase(approveDistributor.fulfilled, (state, action) => {
                const updatedItem = action.payload;
                if (!updatedItem?.Id || !state.distributorList) return;

                const index = state.distributorList.Items.findIndex(
                    (item: any) => item.Id === updatedItem.Id
                );

                if (index !== -1) {
                    state.distributorList.Items[index] = {
                        ...state.distributorList.Items[index],
                        Status: updatedItem.Status,
                    };
                }
            })
            .addCase(getCRMSettings.pending, (state) => {
                state.loading.getCRMSettings = true;
                state.error = null;
            })
            .addCase(getCRMSettings.fulfilled, (state, action) => {
                state.loading.getCRMSettings = false;
                state.crmSettings = action.payload || null;
            })
            .addCase(getCRMSettings.rejected, (state, action: any) => {
                state.loading.getCRMSettings = false;
                state.error = action.payload;
            })

            .addCase(getLeadDesignations.pending, (state) => {
                state.loading.getLeadDesignations = true;
                state.error = null;
            })
            .addCase(getLeadDesignations.fulfilled, (state, action) => {
                state.loading.getLeadDesignations = false;
                state.leadDesignation = action.payload;
            })
            .addCase(getLeadDesignations.rejected, (state, action: any) => {
                state.loading.getLeadDesignations = false;
                state.error = action.payload;
            })

            .addCase(getLeadContacts.pending, (state) => {
                state.loading.getLeadContacts = true;
                state.error = null;
            })
            .addCase(getLeadContacts.fulfilled, (state, action) => {
                state.loading.getLeadContacts = false;
                state.leadContacts = action.payload || [];
            })
            .addCase(getLeadContacts.rejected, (state, action: any) => {
                state.loading.getLeadContacts = false;
                state.error = action.payload;
            })

            .addCase(getAddress.pending, (state) => {
                state.loading.getAddress = true;
                state.error = null;
            })
            .addCase(getAddress.fulfilled, (state, action) => {
                state.loading.getAddress = false;
                state.address = action.payload || [];
            })
            .addCase(getAddress.rejected, (state, action: any) => {
                state.loading.getAddress = false;
                state.error = action.payload;
            })

            .addCase(getCustomerBulkReport.pending, (state) => {
                state.loading.getCustomerBulkReport = true;
                state.error = null;
            })
            .addCase(getCustomerBulkReport.fulfilled, (state, action) => {
                state.loading.getCustomerBulkReport = false;
                state.customerBulkReportList = action.payload || null;
            })
            .addCase(getCustomerBulkReport.rejected, (state, action: any) => {
                state.loading.getCustomerBulkReport = false;
                state.error = action.payload;
            })

            .addCase(viewCustomerBulkReport.pending, (state) => {
                state.loading.viewCustomerBulkReport = true;
                state.error = null;
            })
            .addCase(viewCustomerBulkReport.fulfilled, (state, action) => {
                state.loading.viewCustomerBulkReport = false;
                state.customerBulkReportViewData = action.payload || null;
            })
            .addCase(viewCustomerBulkReport.rejected, (state, action: any) => {
                state.loading.viewCustomerBulkReport = false;
                state.error = action.payload;
            })

            .addCase(getCustomerBulkUploadReport.pending, (state) => {
                state.loading.exportCustomerBulkUploadReport = true;
                state.error = null;
            })
            .addCase(getCustomerBulkUploadReport.fulfilled, (state) => {
                state.loading.exportCustomerBulkUploadReport = false;
            })
            .addCase(getCustomerBulkUploadReport.rejected, (state, action: any) => {
                state.loading.exportCustomerBulkUploadReport = false;
                state.error = action.payload;
            })

            .addCase(getCustomerReportDownloadFile.pending, (state) => {
                state.loading.exportCustomerReportDownloadFile = true;
                state.error = null;
            })
            .addCase(getCustomerReportDownloadFile.fulfilled, (state) => {
                state.loading.exportCustomerReportDownloadFile = false;
            })
            .addCase(getCustomerReportDownloadFile.rejected, (state, action: any) => {
                state.loading.exportCustomerReportDownloadFile = false;
                state.error = action.payload;
            })
            .addCase(getCustomerExport.fulfilled, (state, action) => {
                state.loading.customerExportLoading = false;
            })
            .addCase(getCustomerExport.pending, (state) => {
                state.loading.customerExportLoading = true;
            })
            .addCase(getCustomerExport.rejected, (state) => {
                state.loading.customerExportLoading = false;
            })
            .addCase(getAuditLog.pending, (state) => {
                state.loading.auditLogLoading = true;
            })
            .addCase(getAuditLog.fulfilled, (state, action) => {
                state.auditLog = action.payload || [];
                state.loading.auditLogLoading = false;
            })
            .addCase(getAuditLog.rejected, (state, action: any) => {
                if (action.payload?._aborted) return;
                state.loading.auditLogLoading = false;
            })

            .addCase(exportTransactionBulkUploadFile.pending, (state) => {
                state.loading.exportTransactionReportDownloadFile = true;
                state.error = null;
            })
            .addCase(exportTransactionBulkUploadFile.fulfilled, (state) => {
                state.loading.exportTransactionReportDownloadFile = false;
            })
            .addCase(exportTransactionBulkUploadFile.rejected, (state, action: any) => {
                state.loading.exportTransactionReportDownloadFile = false;
                state.error = action.payload;
            })

            .addCase(viewTransactionBulkReport.pending, (state) => {
                state.loading.viewTransactionBulkReport = true;
                state.error = null;
            })
            .addCase(viewTransactionBulkReport.fulfilled, (state, action) => {
                state.loading.viewTransactionBulkReport = false;
                state.transactionBulkReportViewData = action.payload || null;
            })
            .addCase(viewTransactionBulkReport.rejected, (state, action: any) => {
                state.loading.viewTransactionBulkReport = false;
                state.error = action.payload;
            })

            .addCase(getLeadRequestData.pending, (state) => {
                state.loading.leadRequestLoading = true;
                state.error = null;
            })
            .addCase(getLeadRequestData.fulfilled, (state, action) => {
                state.loading.leadRequestLoading = false;
                state.leadRequest = action.payload || [];
            })
            .addCase(getLeadRequestData.rejected, (state, action: any) => {
                state.loading.leadRequestLoading = false;
                state.error = action.payload;
            })

            .addCase(updateLeadRequest.pending, (state) => {
                state.loading.updateLeadRequestLoading = true;
                state.error = null;
            })
            .addCase(updateLeadRequest.fulfilled, (state, action) => {
                state.loading.updateLeadRequestLoading = false;
                state.updateLeadRequest = action.payload || [];
            })
            .addCase(updateLeadRequest.rejected, (state, action: any) => {
                state.loading.updateLeadRequestLoading = false;
                state.error = action.payload;
            })

            .addCase(getLeadRequestDetail.pending, (state) => {
                state.loading.getLeadRequestDetailLoading = true;
                state.error = null;
            })
            .addCase(getLeadRequestDetail.fulfilled, (state, action) => {
                state.loading.getLeadRequestDetailLoading = false;
                state.customerData = action.payload || {};
            })
            .addCase(getLeadRequestDetail.rejected, (state, action: any) => {
                state.loading.getLeadRequestDetailLoading = false;
                state.error = action.payload;
            });
    }

})
export const { setRefreshCustomerDetails, setRefreshCustomerList, setRefreshLeadRequest } = customerSlice.actions;
export default customerSlice.reducer;